/**
 * Keep aligned with `src/utils/brandIdentifierPolicy.ts`.
 */

import {
  brandCatalogIdentity,
  catalogKeepsIndependentRegionalEan,
  catalogUsesEanAsUpc,
  defaultEanLooksLike,
  defaultSkuLooksLike,
  defaultUpcLooksLike,
  type BarcodeModel,
  type IdentityConfidence,
} from './brandCatalogIdentity';
import { isFujifilmBrand } from './fujifilmProductCodes';
import { isNikonBrand } from './nikonProductCodes';

/** Legacy explicit set — catalog barcodeModel is the live source of truth. */
export const BRANDS_USING_EAN_AS_UPC = new Set(['smallrig', 'viltrox']);

export function brandUsesEanAsUpc(brandId?: string | null, brand?: string | null): boolean {
  if (catalogUsesEanAsUpc(brandId, brand)) return true;
  const id = String(brandId || '')
    .trim()
    .toLowerCase();
  return !!id && BRANDS_USING_EAN_AS_UPC.has(id);
}

export function brandKeepsIndependentRegionalEan(
  brandId?: string | null,
  brand?: string | null,
): boolean {
  return catalogKeepsIndependentRegionalEan(brandId, brand) || isNikonBrand(brand, brandId);
}

export function barcodeModelLabel(model?: BarcodeModel): string | undefined {
  switch (model) {
    case 'ean-as-upc':
      return 'EAN as UPC';
    case 'independent-regional':
      return 'Regional pair';
    case 'upc-a':
      return 'UPC-A';
    default:
      return undefined;
  }
}

export interface BrandIdentifierPolicy {
  brandKey: string;
  useEanAsUpc: boolean;
  independentRegionalEan: boolean;
  fujifilmCatalog: boolean;
  skuLooksLike: string;
  upcLooksLike: string;
  eanLooksLike: string;
  gtinLooksLike: string;
  skuExample?: string;
  mpnRule?: string;
  barcodeModel: BarcodeModel;
  gs1Notes?: string;
  scraperHint?: string;
  confidence?: IdentityConfidence;
}

export function brandIdentifierPolicy(
  brandId?: string | null,
  brand?: string | null,
): BrandIdentifierPolicy {
  const brandKey = String(brandId || brand || '')
    .trim()
    .toLowerCase();
  const catalog = brandCatalogIdentity(brandId, brand);
  const useEanAsUpc = brandUsesEanAsUpc(brandId, brand);
  const independentRegionalEan = brandKeepsIndependentRegionalEan(brandId, brand);
  const fujifilmCatalog = isFujifilmBrand(brand || undefined, brandId || undefined);

  const skuLooksLike = catalog?.skuLooksLike || defaultSkuLooksLike();

  let upcLooksLike = catalog?.upcLooksLike || defaultUpcLooksLike();
  if (useEanAsUpc && !catalog?.upcLooksLike) {
    upcLooksLike =
      'This brand publishes EAN-13 only — a 13-digit value in UPC is normal (not a mistake).';
  } else if (independentRegionalEan && !catalog?.upcLooksLike) {
    upcLooksLike = 'US UPC-A is 12 digits. Do not replace it with a regional EAN/JAN.';
  }

  let eanLooksLike = catalog?.eanLooksLike || defaultEanLooksLike();
  if (independentRegionalEan && !catalog?.eanLooksLike) {
    eanLooksLike =
      'This brand may have a regional EAN/JAN that is a different number from the US UPC. That is normal.';
  }

  const gtinLooksLike =
    '14-digit GS1 number. Leading zeros pad UPC-A / EAN-13 into GTIN-14 (012345678905 → 00012345678905). Do not strip them.';

  return {
    brandKey,
    useEanAsUpc,
    independentRegionalEan,
    fujifilmCatalog,
    skuLooksLike,
    upcLooksLike,
    eanLooksLike,
    gtinLooksLike,
    skuExample: catalog?.skuExample,
    mpnRule: catalog?.mpnRule,
    barcodeModel: catalog?.barcodeModel || 'unknown',
    gs1Notes: catalog?.gs1Notes,
    scraperHint: catalog?.scraperHint,
    confidence: catalog?.confidence,
  };
}
